import {
  DEFAULT_LETTER_PREFERENCES,
  matchLetterFont,
  normalizeFontFamilyName,
} from "./letterPreferences";

/** Inline marks that should stay sticky for the next typed characters. */
const STICKY_INLINE_COMMANDS = new Set([
  "bold",
  "italic",
  "underline",
  "fontname",
  "fontsize",
]);

const INLINE_FORMAT_TAGS = new Set([
  "strong",
  "b",
  "em",
  "i",
  "u",
  "span",
  "font",
]);

const BLOCK_TAG_RE =
  /^(P|DIV|LI|H[1-6]|BLOCKQUOTE|TD|TH|SECTION|ARTICLE|BODY)$/i;

/**
 * Pin character kept inside empty sticky marks (<strong>, <em>, …).
 *
 * Must NOT be stripped by Jodit's trim() / SPACE_REG_EXP, which removes
 * \uFEFF and \u200B — otherwise cleanHTML.removeEmptyElements deletes the
 * mark as soon as the caret leaves. ZWNJ survives that trim.
 */
const STICKY_FORMAT_PIN = "\u200C";

/**
 * Pending typing style: follows the caret across clicks until toggled.
 * Null = inherit format from the caret's DOM context (normal editing).
 *
 * @type {null | {
 *   bold: boolean,
 *   italic: boolean,
 *   fontFamily: string | null,
 *   textStyle: string | null,
 *   fontSize: string | null,
 * }}
 */
let pendingTypingStyle = null;

/** @type {WeakMap<object, () => void>} */
const pendingBindings = new WeakMap();

export function getPendingTypingStyle() {
  return pendingTypingStyle ? { ...pendingTypingStyle } : null;
}

export function hasPendingTypingStyle() {
  return pendingTypingStyle != null;
}

export function clearPendingTypingStyle() {
  pendingTypingStyle = null;
}

function emptyPending() {
  return {
    bold: false,
    italic: false,
    fontFamily: null,
    textStyle: null,
    fontSize: null,
  };
}

/**
 * Update only the field changed by `command` so neighbor DOM formats
 * (e.g. italic "Hello") are never copied into pending.
 */
function updatePendingAfterCommand(command, value) {
  const next = pendingTypingStyle
    ? { ...pendingTypingStyle }
    : emptyPending();

  switch (command) {
    case "bold":
      next.bold = !next.bold;
      break;
    case "italic":
      next.italic = !next.italic;
      break;
    case "underline":
      break;
    case "fontname": {
      const id =
        matchLetterFont(String(value || "").replace(/['"]/g, "")) ||
        String(value || "").replace(/['"]/g, "") ||
        null;
      next.fontFamily = id;
      break;
    }
    case "fontsize": {
      const size = String(value || "").replace(/px$/i, "");
      next.fontSize = size || null;
      next.textStyle = size ? matchTextStyleFromSize(`${size}px`) : null;
      break;
    }
    default:
      break;
  }

  pendingTypingStyle = next;
}

/**
 * Restore a selection saved on blur (e.g. when the mobile format sheet opens)
 * then focus the editor so execCommand applies to the right range.
 */
export function focusAndRestoreSelection(
  editor,
  { preventScroll = true } = {}
) {
  if (!editor) return;

  try {
    if (editor.s?.hasMarkers && typeof editor.s.restore === "function") {
      editor.s.restore();
    }
  } catch {
    // ignore — markers may already be gone
  }

  const editable = editor.editor;
  if (editable && typeof editable.focus === "function") {
    try {
      editable.focus({ preventScroll });
    } catch {
      editable.focus();
    }
  } else if (typeof editor.s?.focus === "function") {
    editor.s.focus();
  } else {
    editor.focus?.();
  }
}

function isInlineFormatElement(node) {
  return (
    node &&
    node.nodeType === Node.ELEMENT_NODE &&
    INLINE_FORMAT_TAGS.has(node.tagName.toLowerCase())
  );
}

function stripStickyPins(value) {
  return String(value || "")
    .split(STICKY_FORMAT_PIN)
    .join("")
    .split("\u200B")
    .join("")
    .replace(/\uFEFF/g, "")
    .replace(/\u00A0/g, "");
}

function isEffectivelyEmptyFormat(el) {
  if (!el) return true;
  if (el.querySelector?.("img, br, hr, video, iframe")) return false;
  return stripStickyPins(el.textContent).trim().length === 0;
}

function placeCaretInPin(selection, doc, pinNode, formatEl) {
  try {
    const range = selection.createRange?.() || doc.createRange();
    range.setStart(pinNode, pinNode.nodeValue?.length ?? 1);
    range.collapse(true);
    selection.selectRange?.(range);
  } catch {
    selection.setCursorIn?.(formatEl, false);
  }
}

function queryCommandStateSafe(editor, command) {
  try {
    const doc = editor.editorDocument || document;
    if (typeof doc.queryCommandState === "function") {
      return Boolean(doc.queryCommandState(command));
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * True when the caret sits mid-word (real text on both sides). In that case
 * we leave local format alone — the user is editing existing text.
 */
function isCaretMidWord(range) {
  if (!range || range.startContainer !== range.endContainer) return false;
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return false;
  const value = node.nodeValue || "";
  const offset = range.startOffset;
  if (offset <= 0 || offset >= value.length) return false;
  const before = stripStickyPins(value.slice(0, offset));
  const after = stripStickyPins(value.slice(offset));
  return before.length > 0 && after.length > 0;
}

/**
 * Move the collapsed caret out of neighbor content marks (e.g. end of italic
 * "Hello") so pending styles are not absorbed into that word's formatting.
 * Returns false if mid-word (caller should skip materialize).
 */
function breakOutOfNeighborFormats(editor) {
  const selection = editor?.s;
  if (!selection || !editor.editor) return false;
  if (!selection.isCollapsed?.()) return false;

  const range = selection.range;
  if (!range) return true;
  if (isCaretMidWord(range)) return false;

  const root = editor.editor;
  let node = range.startContainer;
  let offset = range.startOffset;

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.nodeValue || "";
    const parent = node.parentElement;
    const contentParent =
      parent &&
      isInlineFormatElement(parent) &&
      !isEffectivelyEmptyFormat(parent)
        ? parent
        : null;

    if (contentParent) {
      const atStart = stripStickyPins(text.slice(0, offset)).length === 0;
      const atEnd = stripStickyPins(text.slice(offset)).length === 0;

      let outer = contentParent;
      let walk = contentParent.parentElement;
      while (walk && walk !== root && isInlineFormatElement(walk)) {
        if (!isEffectivelyEmptyFormat(walk)) outer = walk;
        if (BLOCK_TAG_RE.test(walk.tagName)) break;
        walk = walk.parentElement;
      }

      try {
        if (atEnd && !atStart) {
          selection.setCursorAfter?.(outer);
        } else if (atStart && !atEnd) {
          selection.setCursorBefore?.(outer);
        } else if (atStart && atEnd) {
          selection.setCursorAfter?.(outer);
        }
      } catch {
        // ignore
      }
      return true;
    }
  }

  if (node.nodeType === Node.ELEMENT_NODE && offset > 0) {
    const prev = node.childNodes[offset - 1];
    if (isInlineFormatElement(prev) && !isEffectivelyEmptyFormat(prev)) {
      try {
        selection.setCursorAfter?.(prev);
      } catch {
        // ignore
      }
    }
  }

  return true;
}

/**
 * Remove abandoned empty sticky pins so old marks don't litter the DOM.
 * Keeps `keepInside` and any empty sticky chain that currently holds the caret.
 */
function removeOrphanStickyPins(editor, keepInside = null) {
  const root = editor?.editor;
  if (!root) return;

  let caretNode = null;
  try {
    caretNode = editor.s?.current?.() || null;
  } catch {
    caretNode = null;
  }

  const shouldKeep = (node) => {
    if (!node) return false;
    if (keepInside && (node === keepInside || keepInside.contains?.(node) || node.contains?.(keepInside))) {
      return true;
    }
    if (caretNode && (node === caretNode || node.contains?.(caretNode))) {
      return true;
    }
    return false;
  };

  const doomed = [];
  const walk = (node) => {
    if (!node) return;
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (
        isInlineFormatElement(node) &&
        isEffectivelyEmptyFormat(node) &&
        !shouldKeep(node)
      ) {
        doomed.push(node);
        return;
      }
      [...node.childNodes].forEach(walk);
    }
  };
  walk(root);
  doomed.forEach((el) => {
    try {
      el.parentNode?.removeChild(el);
    } catch {
      // ignore
    }
  });
}

/**
 * Read explicit font-family from a sticky span/font mark.
 */
function getMarkFontFamily(el) {
  if (!el) return null;
  const face = el.getAttribute?.("face");
  if (face) return matchLetterFont(face) || normalizeFontFamilyName(face) || null;
  const inline = el.style?.fontFamily;
  if (inline) return matchLetterFont(inline) || normalizeFontFamilyName(inline) || null;
  const data = el.getAttribute?.("data-bbr-font");
  if (data) return matchLetterFont(data) || data;
  return null;
}

function getMarkFontSize(el) {
  if (!el?.style?.fontSize) return null;
  return String(el.style.fontSize).replace(/px$/i, "");
}

/**
 * True when caret is already inside an empty sticky stack matching pending
 * (including the exact font family / size — not just "has a span").
 */
function caretAlreadyHasPending(editor, pending) {
  const root = editor.editor;
  let node = null;
  try {
    node = editor.s?.current?.() || null;
  } catch {
    node = null;
  }
  if (!node) return false;
  let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  let sawBold = false;
  let sawItalic = false;
  let sawFontFamily = null;
  let sawFontSize = null;
  let inEmptySticky = false;

  while (el && el !== root) {
    if (isInlineFormatElement(el) && isEffectivelyEmptyFormat(el)) {
      inEmptySticky = true;
      const tag = el.tagName.toLowerCase();
      if (tag === "strong" || tag === "b") sawBold = true;
      if (tag === "em" || tag === "i") sawItalic = true;
      if (tag === "span" || tag === "font") {
        const markFont = getMarkFontFamily(el);
        if (markFont) sawFontFamily = markFont;
        const markSize = getMarkFontSize(el);
        if (markSize) sawFontSize = markSize;
      }
    } else if (isInlineFormatElement(el) && !isEffectivelyEmptyFormat(el)) {
      // Inside real content — not our sticky pin.
      return false;
    }
    if (BLOCK_TAG_RE.test(el.tagName)) break;
    el = el.parentElement;
  }

  if (!inEmptySticky) return false;
  if (Boolean(pending.bold) !== sawBold) return false;
  if (Boolean(pending.italic) !== sawItalic) return false;

  const wantFont = pending.fontFamily || null;
  if (wantFont) {
    if (!sawFontFamily) return false;
    if (collapseFontKey(wantFont) !== collapseFontKey(sawFontFamily)) {
      return false;
    }
  } else if (sawFontFamily) {
    return false;
  }

  const wantSize = pending.fontSize ? String(pending.fontSize).replace(/px$/i, "") : null;
  if (wantSize) {
    if (!sawFontSize || String(sawFontSize) !== wantSize) return false;
  } else if (sawFontSize) {
    return false;
  }

  return true;
}

function collapseFontKey(value = "") {
  return normalizeFontFamilyName(value).toLowerCase().replace(/\s+/g, " ");
}

function buildPendingFontSpan(doc, pending, inner) {
  const span = doc.createElement("span");
  if (pending.fontFamily) {
    // Quoted multi-word names + cursive fallback (matches workspace style).
    span.style.fontFamily = `"${pending.fontFamily}", cursive`;
    span.setAttribute("data-bbr-font", pending.fontFamily);
  }
  if (pending.fontSize) {
    const size = /^\d+$/.test(String(pending.fontSize))
      ? `${pending.fontSize}px`
      : String(pending.fontSize);
    span.style.fontSize = size;
  }
  span.appendChild(inner);
  return span;
}

/**
 * Build the pending mark stack at the current collapsed caret and park the
 * caret inside it. Call after click/tap/arrow so saved format follows the user.
 */
export function materializePendingTypingStyle(editor) {
  if (!editor?.editor || !pendingTypingStyle) return false;
  const selection = editor.s;
  if (!selection) return false;
  if (!selection.isCollapsed?.()) return false;

  if (!breakOutOfNeighborFormats(editor)) {
    return false;
  }

  const pending = pendingTypingStyle;
  const needsMark =
    pending.bold ||
    pending.italic ||
    pending.fontFamily ||
    pending.fontSize;

  if (needsMark && caretAlreadyHasPending(editor, pending)) {
    saveEditorSelection(editor);
    return true;
  }

  // Drop the current empty sticky chain so a font change (Poppins → Times)
  // cannot leave the caret trapped in the old span.
  removeOrphanStickyPins(editor);
  // Also strip empty sticky marks that still hold the caret (wrong font).
  try {
    let cur = selection.current?.() || null;
    let el = cur?.nodeType === Node.TEXT_NODE ? cur.parentElement : cur;
    const doomed = [];
    while (el && el !== editor.editor) {
      if (isInlineFormatElement(el) && isEffectivelyEmptyFormat(el)) {
        doomed.push(el);
      }
      if (BLOCK_TAG_RE.test(el.tagName)) break;
      el = el.parentElement;
    }
    // Remove innermost-first so parents stay queryable until removed.
    doomed.forEach((node) => {
      try {
        const parent = node.parentNode;
        if (!parent) return;
        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
      } catch {
        // ignore
      }
    });
  } catch {
    // ignore
  }

  if (!needsMark) {
    saveEditorSelection(editor);
    return true;
  }

  const doc = editor.editorDocument || document;
  let pin = doc.createTextNode(STICKY_FORMAT_PIN);
  // Font span innermost so typed characters land directly in the font mark
  // (WebKit often escapes an outer-only empty font span).
  let node = pin;
  if (pending.fontFamily || pending.fontSize) {
    node = buildPendingFontSpan(doc, pending, node);
  }
  if (pending.italic) {
    const em = doc.createElement("em");
    em.appendChild(node);
    node = em;
  }
  if (pending.bold) {
    const strong = doc.createElement("strong");
    strong.appendChild(node);
    node = strong;
  }

  try {
    const range = selection.range;
    if (range) {
      range.insertNode(node);
    } else {
      selection.insertNode?.(node);
    }
    placeCaretInPin(selection, doc, pin, node);
  } catch {
    return false;
  }

  removeOrphanStickyPins(editor, node);
  editor.synchronizeValues?.();
  saveEditorSelection(editor);
  return true;
}

/**
 * Attach click/tap/arrow handlers so pending style re-pins at the new caret.
 * Call once per editor instance (afterInit); returns an unbind function.
 */
export function bindPendingTypingStyle(editor) {
  if (!editor?.editor) return () => {};
  if (pendingBindings.has(editor)) {
    return pendingBindings.get(editor);
  }

  const el = editor.editor;
  let raf = 0;

  const rematerialize = () => {
    if (!pendingTypingStyle) return;
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      materializePendingTypingStyle(editor);
    });
  };

  const onKeyUp = (event) => {
    if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowDown" ||
      event.key === "Home" ||
      event.key === "End" ||
      event.key === "PageUp" ||
      event.key === "PageDown"
    ) {
      rematerialize();
    }
  };

  el.addEventListener("mouseup", rematerialize);
  el.addEventListener("touchend", rematerialize, { passive: true });
  el.addEventListener("keyup", onKeyUp);

  const unbind = () => {
    el.removeEventListener("mouseup", rematerialize);
    el.removeEventListener("touchend", rematerialize);
    el.removeEventListener("keyup", onKeyUp);
    if (raf) cancelAnimationFrame(raf);
    pendingBindings.delete(editor);
  };

  pendingBindings.set(editor, unbind);
  return unbind;
}

export function unbindPendingTypingStyle(editor) {
  const unbind = pendingBindings.get(editor);
  if (unbind) unbind();
}

/**
 * Persist the current caret/selection as markers so it survives UI blur
 * (mobile bottom sheet, etc.).
 */
export function saveEditorSelection(editor) {
  if (!editor?.s || typeof editor.s.save !== "function") return;
  try {
    if (editor.s.hasMarkers) {
      try {
        editor.s.save(true);
      } catch {
        // ignore
      }
      return;
    }
    editor.s.save(true);
  } catch {
    // ignore
  }
}

/**
 * @param {object} [options]
 * @param {boolean} [options.preserveSelection] Re-save markers after the
 *   command so the next mobile toolbar tap still targets the same range.
 * @param {boolean} [options.blurAfter] Blur the editor after applying (keeps
 *   the mobile keyboard closed while the format sheet is open).
 */
export function execEditorCommand(editor, command, value, options = {}) {
  if (!editor) return false;

  const { preserveSelection = false, blurAfter = false } = options;
  const collapsed = editor.s?.isCollapsed?.() !== false;

  focusAndRestoreSelection(editor);

  const finish = () => {
    editor.synchronizeValues?.();
    if (preserveSelection) saveEditorSelection(editor);
    if (blurAfter) {
      try {
        editor.editor?.blur?.();
      } catch {
        // ignore
      }
    }
    return true;
  };

  // Collapsed sticky formats: intent lives in pending and is materialized as
  // DOM marks. Skip execCommand so neighbor DOM state cannot fight the toggle.
  if (collapsed && STICKY_INLINE_COMMANDS.has(command)) {
    breakOutOfNeighborFormats(editor);
    updatePendingAfterCommand(command, value);
    materializePendingTypingStyle(editor);
    return finish();
  }

  let applied = false;

  if (typeof editor.execCommand === "function") {
    const commandValue =
      command === "fontname" && value
        ? `"${String(value).replace(/['"]/g, "")}"`
        : value ?? null;
    editor.execCommand(command, false, commandValue);
    applied = true;
  } else {
    const selection = editor.s;
    if (!selection) return false;

    switch (command) {
      case "bold":
        selection.bold?.();
        break;
      case "italic":
        selection.italic?.();
        break;
      case "underline":
        selection.underline?.();
        break;
      case "justifyLeft":
        selection.applyStyle?.({ "text-align": "left" });
        break;
      case "justifyCenter":
        selection.applyStyle?.({ "text-align": "center" });
        break;
      case "fontname":
        selection.applyStyle?.({
          "font-family": `"${String(value).replace(/['"]/g, "")}"`,
        });
        break;
      case "fontsize":
        selection.applyStyle?.({
          "font-size": /^\d+$/.test(String(value))
            ? `${value}px`
            : String(value),
        });
        break;
      default:
        return false;
    }
    applied = true;
  }

  if (!applied) return false;

  // Range selection: format the highlighted text; clear sticky typing intent.
  if (STICKY_INLINE_COMMANDS.has(command)) {
    clearPendingTypingStyle();
  }

  return finish();
}

export function applyEditorWorkspaceStyle(editor, fontFamily, textStyleSize) {
  if (!editor?.editor) return;
  const root = editor.editor;
  if (root.style.fontFamily !== `"${fontFamily}", cursive`) {
    root.style.fontFamily = `"${fontFamily}", cursive`;
  }
  if (root.style.fontSize !== `${textStyleSize}px`) {
    root.style.fontSize = `${textStyleSize}px`;
  }
  if (root.style.lineHeight !== "1.7") {
    root.style.lineHeight = "1.7";
  }
}

export function setEditorReadOnly(editor, readOnly) {
  if (!editor) return;
  if (typeof editor.setReadOnly === "function") {
    editor.setReadOnly(readOnly);
    return;
  }
  editor.options.readonly = readOnly;
}

function matchTextStyleFromSize(fontSize) {
  const px = parseFloat(fontSize);
  if (!Number.isFinite(px)) return null;
  if (px >= 24) return "title";
  if (px >= 18) return "subtitle";
  return "text";
}

function getSelectionAnchorElement(editor) {
  const doc = editor.editorDocument || document;
  const selection =
    editor.s?.sel ||
    doc.getSelection?.() ||
    (typeof window !== "undefined" ? window.getSelection?.() : null);

  let node = selection?.anchorNode || null;
  if (node?.nodeType === Node.TEXT_NODE) {
    node = node.parentElement;
  }
  if (node && editor.editor.contains(node)) {
    return node;
  }

  try {
    if (editor.s?.hasMarkers) {
      const marker = editor.s.markers?.[0];
      if (marker && editor.editor.contains(marker)) {
        return marker.parentElement || marker;
      }
    }
  } catch {
    // ignore
  }

  return editor.editor;
}

function getExplicitFontFamily(node, root) {
  let el = node;
  while (el) {
    const inline = el.style?.fontFamily;
    if (inline) return normalizeFontFamilyName(inline);

    const face = el.getAttribute?.("face");
    if (face) return normalizeFontFamilyName(face);

    if (el === root) break;
    el = el.parentElement;
  }
  return null;
}

function queryCommandFontName(editor) {
  try {
    const doc = editor.editorDocument || document;
    if (typeof doc.queryCommandValue === "function") {
      const value = doc.queryCommandValue("fontName");
      if (value && value !== "false") {
        return normalizeFontFamilyName(value);
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function isNumericFontWeightBold(weight) {
  const n = Number(weight);
  return Number.isFinite(n) && n >= 600;
}

function hasBoldMark(node, root) {
  let el = node;
  while (el && el !== root) {
    const tag = el.tagName?.toLowerCase();
    if (tag === "b" || tag === "strong") return true;

    const inlineWeight = el.style?.fontWeight;
    if (
      inlineWeight &&
      (inlineWeight === "bold" ||
        inlineWeight === "bolder" ||
        isNumericFontWeightBold(inlineWeight))
    ) {
      return true;
    }

    el = el.parentElement;
  }
  return false;
}

function hasItalicMark(node, root) {
  let el = node;
  while (el && el !== root) {
    const tag = el.tagName?.toLowerCase();
    if (tag === "i" || tag === "em") return true;

    const inlineStyle = el.style?.fontStyle;
    if (inlineStyle === "italic" || inlineStyle === "oblique") return true;

    el = el.parentElement;
  }
  return false;
}

function readDomSelectionState(editor) {
  const root = editor.editor;
  const node = getSelectionAnchorElement(editor);
  const styles = window.getComputedStyle(node);
  const rootStyles = window.getComputedStyle(root);
  const align = (styles.textAlign || "left").toLowerCase();
  const textStyle = matchTextStyleFromSize(styles.fontSize);

  const explicitFont = getExplicitFontFamily(node, root);
  const commandFont = queryCommandFontName(editor);
  const computedFont = normalizeFontFamilyName(styles.fontFamily);
  const rootFont = normalizeFontFamilyName(rootStyles.fontFamily);

  const fontFamily =
    matchLetterFont(explicitFont) ||
    matchLetterFont(commandFont) ||
    matchLetterFont(computedFont) ||
    matchLetterFont(rootFont) ||
    DEFAULT_LETTER_PREFERENCES.fontFamily;

  const boldFromDom = hasBoldMark(node, root);
  const italicFromDom = hasItalicMark(node, root);
  const boldCmd = queryCommandStateSafe(editor, "bold");
  const italicCmd = queryCommandStateSafe(editor, "italic");

  const bold =
    boldFromDom ||
    (boldCmd === null
      ? isNumericFontWeightBold(styles.fontWeight) ||
        styles.fontWeight === "bold"
      : boldCmd);
  const italic =
    italicFromDom ||
    (italicCmd === null ? styles.fontStyle === "italic" : italicCmd);

  return {
    bold,
    italic,
    left: align === "left" || align === "start",
    center: align === "center",
    fontFamily,
    textStyle,
  };
}

/**
 * Reads formatting at the current caret/selection for sidebar sync.
 * While a pending typing style is active, bold/italic/font/size come from
 * pending so a click next to a neighbor word cannot wipe the saved format UI.
 */
export function getEditorSelectionState(editor) {
  if (!editor?.editor || typeof window === "undefined") {
    return {
      bold: false,
      italic: false,
      left: false,
      center: false,
      fontFamily: DEFAULT_LETTER_PREFERENCES.fontFamily,
      textStyle: null,
    };
  }

  const domState = readDomSelectionState(editor);
  const pending = pendingTypingStyle;

  if (!pending) return domState;

  return {
    ...domState,
    bold: pending.bold,
    italic: pending.italic,
    fontFamily: pending.fontFamily || domState.fontFamily,
    textStyle:
      pending.textStyle ||
      (pending.fontSize
        ? matchTextStyleFromSize(`${pending.fontSize}px`)
        : domState.textStyle),
  };
}
